import { useEffect, useMemo, useState, type SVGProps } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSelectedCity } from '../hooks/useSelectedCity';

type NavigationItem = {
  label: string;
  description: string;
  to: string;
  end?: boolean;
  isActive?: (pathname: string) => boolean;
  icon: (props: SVGProps<SVGSVGElement>) => JSX.Element;
};

const resolveCurrentPageLabel = (pathname: string) => {
  if (pathname === '/') {
    return 'Panel';
  }
  if (pathname.startsWith('/parking/')) {
    return 'Szczegoly';
  }
  if (pathname.startsWith('/admin/parking/new')) {
    return 'Nowy parking';
  }
  if (pathname.startsWith('/admin/vehicles/new')) {
    return 'Pojazdy';
  }
  if (pathname.startsWith('/admin/cameras')) {
    return 'Kamery';
  }
  if (pathname.startsWith('/register')) {
    return 'Rejestracja';
  }
  if (pathname.startsWith('/login')) {
    return 'Logowanie';
  }

  return 'Menu';
};

export function MobileAppNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, logout, userEmail } = useAuth();
  const { cities, isCitiesLoading, selectedCityId, selectedCityName, setSelectedCityId } = useSelectedCity();
  const [isOpen, setIsOpen] = useState(false);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');

  useEffect(() => {
    setIsOpen(false);
    setIsCityModalOpen(false);
    setCitySearch('');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isOpen && !isCityModalOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      if (isCityModalOpen) {
        setIsCityModalOpen(false);
        return;
      }

      setIsOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isCityModalOpen, isOpen]);

  const currentPageLabel = useMemo(() => resolveCurrentPageLabel(location.pathname), [location.pathname]);

  const navigationItems = useMemo<NavigationItem[]>(() => {
    if (!isAuthenticated) {
      return [
        {
          label: 'Logowanie',
          description: 'Wejdz do systemu',
          to: '/login',
          end: true,
          icon: LoginIcon,
        },
        {
          label: 'Rejestracja',
          description: 'Utwórz konto',
          to: '/register',
          icon: UserPlusIcon,
        },
      ];
    }

    const items: NavigationItem[] = [
      {
        label: 'Panel',
        description: 'Lista parkingow',
        to: '/',
        end: true,
        icon: HomeIcon,
      },
      {
        label: 'Pojazdy',
        description: 'Zarzadzaj pojazdami',
        to: '/admin/vehicles/new',
        icon: CarIcon,
      },
    ];

    if (isAdmin) {
      items.push(
        {
          label: 'Parking',
          description: 'Dodaj lub edytuj parking',
          to: '/admin/parking/new',
          icon: MapIcon,
        },
        {
          label: 'Kamery',
          description: 'Zarzadzaj kamera',
          to: '/admin/cameras',
          icon: CameraIcon,
        },
      );
    }

    return items;
  }, [isAdmin, isAuthenticated]);

  const filteredCities = useMemo(() => {
    const normalizedQuery = citySearch.trim().toLowerCase();
    if (!normalizedQuery) {
      return cities;
    }

    return cities.filter((city) => city.name.toLowerCase().includes(normalizedQuery));
  }, [cities, citySearch]);

  const openCityModal = () => {
    setIsOpen(false);
    setCitySearch('');
    setIsCityModalOpen(true);
  };

  const handleLogout = () => {
    setIsOpen(false);
    setIsCityModalOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  const statusLabel = isAuthenticated ? (isAdmin ? 'Admin' : 'Online') : 'Gość';

  const handleCitySelect = (cityId: string) => {
    setSelectedCityId(cityId);
    setIsOpen(false);
    setIsCityModalOpen(false);
    setCitySearch('');

    if (location.pathname.startsWith('/parking/')) {
      navigate('/');
    }
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40 border-b border-slate-200/80 bg-white/88 shadow-[0_12px_32px_-24px_rgba(15,23,42,0.55)] backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#181818]/92 md:hidden">
        <div className="mx-auto flex h-[72px] w-full max-w-[1920px] items-center justify-between gap-3 px-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => setIsOpen((prev) => !prev)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-slate-700 transition hover:border-emerald-400 hover:bg-emerald-500 hover:text-white dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-zinc-100 dark:hover:border-emerald-400 dark:hover:bg-emerald-500"
              aria-label={isOpen ? 'Zamknij nawigację' : 'Otwórz nawigację'}
              aria-expanded={isOpen}
              aria-controls="mobile-app-drawer"
            >
              <MenuIcon className={`h-5 w-5 transition-transform duration-300 ${isOpen ? 'rotate-90 scale-90' : ''}`} />
            </button>

            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-emerald-600 dark:text-emerald-300">Parking Box</p>
              <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{currentPageLabel}</p>
            </div>
          </div>

          {isAuthenticated ? (
            <button
              type="button"
              onClick={openCityModal}
              className="flex max-w-[10.5rem] shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-emerald-400 hover:bg-emerald-50 dark:border-white/[0.08] dark:bg-white/[0.05] dark:hover:border-emerald-400 dark:hover:bg-white/[0.08]"
              aria-label="Otworz wybor miasta"
            >
              <PinIcon className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-300" />
              <span className="min-w-0">
                <span className="block text-[9px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-300/75">Wybrano</span>
                <span className="block truncate text-xs font-semibold text-slate-700 dark:text-slate-100">{selectedCityName ?? 'Miasto'}</span>
              </span>
            </button>
          ) : (
            <div className="max-w-[8.75rem] shrink-0 truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-slate-200">
              {statusLabel}
            </div>
          )}
        </div>
      </header>

      <div className={`fixed inset-0 z-[60] md:hidden ${isOpen ? 'pointer-events-auto' : 'pointer-events-none'}`} aria-hidden={!isOpen}>
        <div
          className={`absolute inset-0 bg-slate-900/34 backdrop-blur-sm transition-opacity duration-300 dark:bg-black/68 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsOpen(false)}
        />

        <aside
          id="mobile-app-drawer"
          className={`absolute inset-y-0 left-0 flex w-[320px] max-w-[88vw] flex-col overflow-hidden rounded-r-[32px] border-r border-slate-200 bg-[linear-gradient(180deg,#f8fafc_0%,#ffffff_24%,#f8fafc_100%)] text-slate-900 shadow-[0_28px_80px_-30px_rgba(15,23,42,0.45)] transform-gpu transition duration-300 ease-out dark:border-white/[0.08] dark:bg-[linear-gradient(180deg,#1a1a1a_0%,#202020_26%,#202020_100%)] dark:text-white dark:shadow-[0_28px_80px_-30px_rgba(0,0,0,0.72)] ${
            isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
          }`}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top,#bbf7d0_0%,#ecfdf5_18%,#ffffff_68%)] px-5 py-5 dark:border-white/[0.08] dark:bg-[radial-gradient(circle_at_top,#24583f_0%,#1f3429_18%,#202020_62%,#202020_100%)]">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-700 dark:text-emerald-200/75">Parking Box</p>
                <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">{currentPageLabel}</h2>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-full border border-slate-200 bg-white/80 p-2 text-slate-500 transition hover:border-emerald-400 hover:text-emerald-600 dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-slate-200 dark:hover:border-emerald-300 dark:hover:bg-white/[0.1] dark:hover:text-white"
                aria-label="Zamknij nawigację"
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            </div>

            {isAuthenticated ? (
              <div className="mt-5 rounded-[24px] border border-slate-200 bg-white/90 px-4 py-3 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.3)] backdrop-blur dark:border-white/[0.08] dark:bg-white/[0.04] dark:shadow-[0_18px_40px_-32px_rgba(0,0,0,0.55)]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/80">Aktywna sesja</p>
                <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-white">{userEmail ?? 'Zalogowany użytkownik'}</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-300/80">{isAdmin ? 'Uprawnienia administratora' : 'Konto użytkownika'}</p>
              </div>
            ) : null}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            <div className="space-y-2">
              {navigationItems.map((item) => {
                const isCurrent = item.isActive ? item.isActive(location.pathname) : false;

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 rounded-[24px] border px-4 py-3 transition duration-200',
                        isActive || isCurrent
                          ? 'border-emerald-300 bg-emerald-50 text-slate-900 shadow-[0_18px_32px_-24px_rgba(16,185,129,0.3)] dark:border-emerald-400/30 dark:bg-[#262626] dark:bg-[linear-gradient(180deg,rgba(16,185,129,0.14)_0%,rgba(16,185,129,0.04)_100%)] dark:text-white dark:shadow-[0_18px_32px_-24px_rgba(16,185,129,0.18)]'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-slate-50 dark:border-white/[0.08] dark:bg-white/[0.035] dark:text-zinc-200 dark:hover:border-emerald-400/35 dark:hover:bg-white/[0.06]',
                      ].join(' ')
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition duration-200 ${
                            isActive || isCurrent
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-100 text-slate-500 dark:bg-white/[0.08] dark:text-slate-200'
                          }`}
                        >
                          <item.icon className="h-5 w-5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold">{item.label}</span>
                          <span className="block text-xs text-slate-400 dark:text-slate-300/75">{item.description}</span>
                        </span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>

            {location.pathname.startsWith('/parking/') ? (
              <div className="mt-5 rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4 dark:border-white/[0.08] dark:bg-white/[0.035]">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-300/80">Bieżący widok</p>
                <p className="mt-2 text-sm font-semibold text-slate-800 dark:text-white">Szczegóły parkingu</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-300/75">Wróć do panelu, aby zmienić miasto albo otworzyć inny parking.</p>
                <Link
                  to="/"
                  onClick={() => setIsOpen(false)}
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-white px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-600 transition hover:border-emerald-400 hover:bg-emerald-50 dark:border-emerald-400/35 dark:bg-white/[0.03] dark:text-emerald-200 dark:hover:border-emerald-300 dark:hover:bg-emerald-500/12"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Wróć do panelu
                </Link>
              </div>
            ) : null}
          </div>

          <div className="border-t border-slate-200 px-4 py-4 dark:border-white/[0.08]">
            {isAuthenticated ? (
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center justify-center gap-2 rounded-[20px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-600 transition hover:border-rose-300 hover:bg-rose-100 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-200 dark:hover:border-rose-300 dark:hover:bg-rose-500/16"
              >
                <LogoutIcon className="h-4 w-4" />
                Wyloguj
              </button>
            ) : (
              <Link
                to={location.pathname.startsWith('/register') ? '/login' : '/register'}
                onClick={() => setIsOpen(false)}
                className="flex w-full items-center justify-center gap-2 rounded-[20px] border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:border-emerald-400 hover:bg-emerald-100 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-100 dark:hover:border-emerald-300 dark:hover:bg-emerald-500/16"
              >
                {location.pathname.startsWith('/register') ? 'Przejdź do logowania' : 'Utwórz konto'}
              </Link>
            )}
          </div>
        </aside>
      </div>

      <div className={`fixed inset-0 z-[70] md:hidden ${isCityModalOpen ? 'pointer-events-auto' : 'pointer-events-none'}`} aria-hidden={!isCityModalOpen}>
        <div
          className={`absolute inset-0 bg-slate-900/38 backdrop-blur-sm transition-opacity duration-300 dark:bg-black/72 ${isCityModalOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setIsCityModalOpen(false)}
        />

        <div className="absolute inset-x-4 bottom-4 top-[88px]">
          <section
            role="dialog"
            aria-modal="true"
            aria-label="Wybor miasta"
            className={`flex h-full flex-col overflow-hidden rounded-[32px] border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] shadow-[0_35px_80px_-35px_rgba(15,23,42,0.38)] transform-gpu transition duration-300 ease-out dark:border-white/[0.08] dark:bg-[linear-gradient(180deg,#1a1a1a_0%,#202020_26%,#202020_100%)] ${
              isCityModalOpen ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-6 opacity-0 scale-[0.98]'
            }`}
          >
            <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top,#bbf7d0_0%,#ecfdf5_18%,#ffffff_65%)] px-5 py-5 dark:border-white/[0.08] dark:bg-[radial-gradient(circle_at_top,#24583f_0%,#1f3429_18%,#202020_62%,#202020_100%)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-emerald-700 dark:text-emerald-200/80">Miasta</p>
                  <h2 className="mt-2 truncate text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                    {selectedCityName ? `Wybrano: ${selectedCityName}` : 'Wybierz miasto'}
                  </h2>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-300/75">Lista jest przewijana i gotowa na wieksza liczbe lokalizacji.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsCityModalOpen(false)}
                  className="rounded-full border border-slate-200 bg-white/80 p-2 text-slate-500 transition hover:border-emerald-400 hover:text-emerald-600 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-slate-200 dark:hover:bg-white/[0.1]"
                  aria-label="Zamknij okno wyboru miasta"
                >
                  <CloseIcon className="h-4 w-4" />
                </button>
              </div>

              <label className="mt-5 block">
                <span className="sr-only">Szukaj miasta</span>
                <div className="flex items-center gap-3 rounded-[20px] border border-slate-200 bg-white px-4 py-3 shadow-[0_16px_30px_-24px_rgba(15,23,42,0.25)] dark:border-white/[0.08] dark:bg-white/[0.05] dark:shadow-[0_16px_30px_-24px_rgba(0,0,0,0.5)]">
                  <SearchIcon className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-300/70" />
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(event) => setCitySearch(event.target.value)}
                    placeholder="Szukaj miasta"
                    className="w-full border-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-400/80"
                  />
                </div>
              </label>
            </div>

            <div className="flex items-center justify-between border-b border-slate-200 bg-white/70 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400 dark:border-white/[0.08] dark:bg-black/10 dark:text-slate-300/75">
              <span>{isCitiesLoading ? 'Ladowanie miast' : `${filteredCities.length} wynikow`}</span>
              {selectedCityName ? <span className="truncate text-emerald-600 dark:text-emerald-300">{selectedCityName}</span> : null}
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {isCitiesLoading ? (
                <div className="flex h-full min-h-[16rem] items-center justify-center text-center text-sm text-slate-500 dark:text-slate-300/80">
                  Trwa pobieranie listy miast...
                </div>
              ) : filteredCities.length > 0 ? (
                <div className="space-y-2">
                  {filteredCities.map((city) => {
                    const isActive = city.id === selectedCityId;

                    return (
                      <button
                        key={city.id}
                        type="button"
                        onClick={() => handleCitySelect(city.id)}
                        className={`flex w-full items-center justify-between gap-3 rounded-[22px] border px-4 py-4 text-left transition ${
                          isActive
                            ? 'border-emerald-300 bg-emerald-50 text-slate-900 shadow-[0_18px_36px_-24px_rgba(16,185,129,0.28)] dark:border-emerald-400/30 dark:bg-[#262626] dark:bg-[linear-gradient(180deg,rgba(16,185,129,0.14)_0%,rgba(16,185,129,0.04)_100%)] dark:text-white dark:shadow-[0_18px_36px_-24px_rgba(16,185,129,0.18)]'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-slate-50 dark:border-white/[0.08] dark:bg-white/[0.035] dark:text-slate-100 dark:hover:border-emerald-400/35 dark:hover:bg-white/[0.06]'
                        }`}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold">{city.name}</span>
                          <span className="mt-1 block text-[11px] uppercase tracking-[0.22em] text-slate-400 dark:text-slate-300/70">
                            {isActive ? 'Aktualnie wybrane' : 'Dotknij, aby wybrac'}
                          </span>
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] ${
                            isActive ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-500 dark:bg-white/[0.08] dark:text-slate-300'
                          }`}
                        >
                          {isActive ? 'Wybrane' : 'Wybierz'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex h-full min-h-[16rem] flex-col items-center justify-center text-center">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-100">Brak dopasowanych miast</p>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-300/75">Zmien fraze wyszukiwania albo wyczysc filtr.</p>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function MenuIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h12" />
    </svg>
  );
}

function CloseIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function SearchIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function PinIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M12 21s6-4.35 6-10a6 6 0 1 0-12 0c0 5.65 6 10 6 10Z" />
      <circle cx="12" cy="11" r="2.5" />
    </svg>
  );
}

function HomeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20h13V9.5" />
    </svg>
  );
}

function CarIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M4 15V9.5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2V15" />
      <path d="M3 15h18" />
      <path d="M6 18h.01" />
      <path d="M18 18h.01" />
      <path d="M5 15v3" />
      <path d="M19 15v3" />
    </svg>
  );
}

function MapIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20V6.5Z" />
      <path d="M9 4v13.5" />
      <path d="M15 6.5V20" />
    </svg>
  );
}

function CameraIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M4 8h4l2-2h4l2 2h4v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

function LoginIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M15 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3" />
      <path d="M10 17 15 12 10 7" />
      <path d="M15 12H4" />
    </svg>
  );
}

function UserPlusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
      <circle cx="9.5" cy="7" r="4" />
      <path d="M19 8v6" />
      <path d="M16 11h6" />
    </svg>
  );
}

function ArrowLeftIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}

function LogoutIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}
